import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  RetirementCalc, DebtCalc, InvestmentCalc, CompoundCalc,
  EmergencyCalc, MortgageCalc, NetWorthCalc, SavingsRateCalc, CollegeFundCalc
} from './Calculators';

const CALC_META = [
  {
    id: 'retirement',
    emoji: '🏖️',
    title: 'Retirement Savings Calculator',
    subtitle: 'How much will you have by retirement?',
    description: 'Enter your age, savings, and contributions to see exactly how much you\'ll have at retirement and whether you\'re on track.',
    seoTitle: 'Free Retirement Savings Calculator | KnowFi',
    seoDesc: 'Calculate how much you\'ll have at retirement. Free retirement savings calculator — enter your age, current savings, monthly contributions, and expected return.',
    keywords: 'retirement calculator, retirement savings calculator, how much to save for retirement, 401k calculator',
    Component: RetirementCalc,
  },
  {
    id: 'debt',
    emoji: '💳',
    title: 'Debt Payoff Calculator',
    subtitle: 'See exactly when you\'ll be debt free',
    description: 'Enter your debts, interest rates, and monthly payment to see your payoff date and total interest saved.',
    seoTitle: 'Free Debt Payoff Calculator | KnowFi',
    seoDesc: 'Calculate your debt payoff date and total interest cost. Free debt payoff calculator for credit cards, loans, and more.',
    keywords: 'debt payoff calculator, credit card payoff calculator, when will I be debt free, debt calculator',
    Component: DebtCalc,
  },
  {
    id: 'mortgage',
    emoji: '🏠',
    title: 'Mortgage Payment Calculator',
    subtitle: 'What will your monthly payment be?',
    description: 'Calculate your monthly mortgage payment, total interest paid, and see a full amortization breakdown.',
    seoTitle: 'Free Mortgage Payment Calculator | KnowFi',
    seoDesc: 'Calculate your monthly mortgage payment, total interest, and amortization schedule. Free mortgage calculator for home buyers.',
    keywords: 'mortgage calculator, mortgage payment calculator, monthly mortgage payment, home loan calculator',
    Component: MortgageCalc,
  },
  {
    id: 'net-worth',
    emoji: '📊',
    title: 'Net Worth Calculator',
    subtitle: 'Assets minus liabilities — your real financial picture',
    description: 'Add up what you own and what you owe to calculate your true net worth.',
    seoTitle: 'Free Net Worth Calculator | KnowFi',
    seoDesc: 'Calculate your net worth for free. Enter your assets and liabilities to see your complete financial picture.',
    keywords: 'net worth calculator, calculate net worth, assets minus liabilities, personal net worth',
    Component: NetWorthCalc,
  },
  {
    id: 'savings-rate',
    emoji: '💰',
    title: 'Savings Rate Calculator',
    subtitle: 'What percentage of your income are you saving?',
    description: 'Calculate your personal savings rate and see how it compares to recommended benchmarks.',
    seoTitle: 'Free Savings Rate Calculator | KnowFi',
    seoDesc: 'Calculate your personal savings rate. See what percentage of your income you\'re saving and how to improve it.',
    keywords: 'savings rate calculator, personal savings rate, how much should I save, savings percentage income',
    Component: SavingsRateCalc,
  },
  {
    id: 'college-fund',
    emoji: '🎓',
    title: 'College Fund Calculator',
    subtitle: 'How much do you need to save for college?',
    description: 'Calculate how much to save monthly to hit your college savings goal by the time your child enrolls.',
    seoTitle: 'Free College Savings Calculator | KnowFi',
    seoDesc: 'Calculate how much to save for college. Free 529 and college fund calculator — see your monthly savings target.',
    keywords: 'college savings calculator, 529 calculator, how much to save for college, college fund calculator',
    Component: CollegeFundCalc,
  },
  {
    id: 'investment',
    emoji: '📈',
    title: 'Investment Growth Calculator',
    subtitle: 'Watch your money compound over time',
    description: 'Enter your initial investment, monthly contributions, and expected return to see how your portfolio grows.',
    seoTitle: 'Free Investment Growth Calculator | KnowFi',
    seoDesc: 'Calculate investment growth over time. Free investment calculator — see how compound growth builds wealth.',
    keywords: 'investment calculator, investment growth calculator, compound growth calculator, portfolio growth',
    Component: InvestmentCalc,
  },
  {
    id: 'compound-interest',
    emoji: '✨',
    title: 'Compound Interest Calculator',
    subtitle: 'The 8th wonder of the world — calculated',
    description: 'See how compound interest grows your money exponentially over time with different rates and timeframes.',
    seoTitle: 'Free Compound Interest Calculator | KnowFi',
    seoDesc: 'Calculate compound interest for any investment. Free compound interest calculator — see how interest compounds daily, monthly, or annually.',
    keywords: 'compound interest calculator, compound interest formula, how does compound interest work, compound growth calculator',
    Component: CompoundCalc,
  },
  {
    id: 'emergency-fund',
    emoji: '🛡️',
    title: 'Emergency Fund Calculator',
    subtitle: 'How much do you actually need?',
    description: 'Calculate your target emergency fund size based on your real monthly expenses.',
    seoTitle: 'Free Emergency Fund Calculator | KnowFi',
    seoDesc: 'Calculate how much emergency fund you need. Free emergency savings calculator — see your 3-month and 6-month targets.',
    keywords: 'emergency fund calculator, how much emergency fund, emergency savings calculator, 3 months expenses',
    Component: EmergencyCalc,
  },
];

export default function CalculatorDetail() {
  const { id } = useParams();
  const calc = CALC_META.find(c => c.id === id);

  if (!calc) {
    return (
      <div style={{ minHeight: '100vh', background: '#0f172a', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', color: '#94a3b8', gap: 16 }}>
        <div style={{ fontSize: 48 }}>🔢</div>
        <h2 style={{ color: '#f1f5f9', margin: 0 }}>Calculator not found</h2>
        <Link to="/calculators" style={{ color: '#38bdf8', textDecoration: 'none' }}>← All Calculators</Link>
      </div>
    );
  }

  const { Component } = calc;

  const s = {
    page: { minHeight: '100vh', background: '#0f172a', color: '#f1f5f9', padding: '32px 16px 80px', fontFamily: 'system-ui, sans-serif' },
    inner: { maxWidth: 860, margin: '0 auto' },
    back: { display: 'inline-flex', alignItems: 'center', gap: 6, color: '#94a3b8', textDecoration: 'none', fontSize: 14, marginBottom: 28 },
    hero: { textAlign: 'center', marginBottom: 36, padding: '0 8px' },
    emoji: { fontSize: 52, marginBottom: 12 },
    title: { fontSize: 'clamp(22px, 5vw, 34px)', fontWeight: 800, margin: '0 0 10px', background: 'linear-gradient(135deg, #38bdf8, #a78bfa)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' },
    subtitle: { fontSize: 17, color: '#94a3b8', margin: '0 0 6px' },
    desc: { fontSize: 14, color: '#64748b', margin: 0 },
    cta: { background: 'linear-gradient(135deg, rgba(56,189,248,.1), rgba(167,139,250,.1))', border: '1px solid rgba(56,189,248,.2)', borderRadius: 16, padding: '28px 32px', textAlign: 'center', marginTop: 40 },
    ctaTitle: { fontSize: 20, fontWeight: 700, color: '#f1f5f9', marginBottom: 8 },
    ctaSub: { color: '#94a3b8', fontSize: 15, marginBottom: 20 },
    btns: { display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' },
    btn: { background: 'linear-gradient(135deg, #38bdf8, #a78bfa)', color: '#0f172a', padding: '12px 24px', borderRadius: 10, fontWeight: 700, fontSize: 15, textDecoration: 'none', display: 'inline-block' },
    btnSec: { background: 'rgba(255,255,255,.06)', color: '#f1f5f9', padding: '12px 24px', borderRadius: 10, fontWeight: 600, fontSize: 15, textDecoration: 'none', display: 'inline-block', border: '1px solid rgba(255,255,255,.1)' },
  };

  return (
    <div style={s.page}>
      <Helmet>
        <title>{calc.seoTitle}</title>
        <meta name="description" content={calc.seoDesc} />
        <meta name="keywords" content={calc.keywords} />
        <meta property="og:title" content={calc.seoTitle} />
        <meta property="og:description" content={calc.seoDesc} />
        <link rel="canonical" href={`https://knowfi.io/calculators/${calc.id}`} />
      </Helmet>

      <div style={s.inner}>
        <Link to="/calculators" style={s.back}>← All Calculators</Link>

        <div style={s.hero}>
          <div style={s.emoji}>{calc.emoji}</div>
          <h1 style={s.title}>{calc.title}</h1>
          <p style={s.subtitle}>{calc.subtitle}</p>
          <p style={s.desc}>{calc.description}</p>
        </div>

        <Component />

        <div style={s.cta}>
          <div style={s.ctaTitle}>Want a full financial plan?</div>
          <div style={s.ctaSub}>Answer 4 questions and get a personalized money plan for your situation.</div>
          <div style={s.btns}>
            <Link to="/gameplan" style={s.btn}>🎯 Build My Game Plan</Link>
            <Link to="/calculators" style={s.btnSec}>← All Calculators</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
